import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Upload } from 'lucide-react'
import {
  Button,
  DataTable,
  EmptyState,
  StatusBadge,
  dateColumn,
  textColumn,
  type ColumnDef,
} from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { useContracts } from '../api/contractsApi.ts'
import type { ContractDirection, ContractListItem } from '../types.ts'
import { CONTRACT_CATEGORY_LABELS } from '../types.ts'
import { ContractFormModal } from '../components/ContractFormModal.tsx'
import { BulkImportModal } from '../components/BulkImportModal.tsx'

const PERSONNEL_ROLES = new Set(['admin', 'hr', 'gf'])

interface ContractListSectionProps {
  /** Wenn gesetzt: Liste auf outbound/inbound einschraenken und Anlage entsprechend voreinstellen. */
  defaultDirection?: ContractDirection
  /** EmptyState-Hinweis fuer leere Liste. */
  emptyHint?: string
}

export function ContractListSection({ defaultDirection, emptyHint }: ContractListSectionProps = {}) {
  const a = useAnonymize()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const { data: rows = [], isLoading, error, refetch } = useContracts(
    defaultDirection ? { direction: defaultDirection } : {},
  )
  const userRole = useAuthStore((s) => s.user?.role ?? '')
  const canSeePersonnel = PERSONNEL_ROLES.has(userRole)

  const columns = useMemo<ColumnDef<ContractListItem, unknown>[]>(
    () => [
      textColumn<ContractListItem>('contractNumber', 'Vertragsnr.', { size: 130 }),
      // Richtungs-Spalte nur sichtbar im finance-Kontext (gemischte Liste).
      ...(defaultDirection
        ? []
        : [
            {
              accessorKey: 'direction',
              header: 'Richtung',
              size: 110,
              cell: ({ getValue }: { getValue: () => unknown }) => (
                <StatusBadge status={`contract_${getValue() as string}`} />
              ),
            } as ColumnDef<ContractListItem, unknown>,
          ]),
      // Vertragsart-Spalte nur fuer Rollen mit Personal-Zugriff (zeigt dann alle Kategorien).
      ...(canSeePersonnel
        ? [
            {
              accessorKey: 'contractCategory',
              header: 'Vertragsart',
              size: 140,
              cell: ({ getValue }: { getValue: () => unknown }) =>
                CONTRACT_CATEGORY_LABELS[getValue() as keyof typeof CONTRACT_CATEGORY_LABELS] ?? String(getValue()),
            } as ColumnDef<ContractListItem, unknown>,
          ]
        : []),
      {
        accessorKey: 'customerName',
        header: 'Partei',
        cell: ({ row }) => {
          const display = row.original.customerName ?? row.original.partyName ?? '—'
          return <span className="text-small font-medium text-gray-900">{a.name(display)}</span>
        },
      },
      textColumn<ContractListItem>('productName', 'Produkt'),
      textColumn<ContractListItem>('name', 'Bezeichnung'),
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ getValue }) => <StatusBadge status={`contract_${getValue() as string}`} />,
      },
      dateColumn<ContractListItem>('startDate', 'Beginn', { size: 110 }),
      dateColumn<ContractListItem>('endDate', 'Ende', { size: 110 }),
    ],
    [a, canSeePersonnel, defaultDirection],
  )

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" leadingIcon={<Upload className="w-4 h-4" />} onClick={() => setImportOpen(true)}>
          CSV importieren
        </Button>
        <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
          Neuer Vertrag
        </Button>
      </div>

      <DataTable<ContractListItem>
        data={rows}
        columns={columns}
        getRowId={(c) => c.id}
        loading={isLoading}
        error={error ? 'Verträge konnten nicht geladen werden.' : null}
        onRetry={() => void refetch()}
        searchPlaceholder="Verträge suchen …"
        onRowClick={(c) => navigate(`/vertraege/${c.id}`)}
        emptyState={
          <EmptyState
            title="Noch keine Verträge"
            description={emptyHint ?? 'Lege deinen ersten Vertrag an. Stammdaten, Positionen und Dokumente verwaltest du dann pro Vertrag.'}
            action={
              <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                Neuer Vertrag
              </Button>
            }
          />
        }
      />

      <ContractFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultDirection={defaultDirection}
        onCreated={(c) => {
          setCreateOpen(false)
          navigate(`/vertraege/${c.id}`)
        }}
      />

      <BulkImportModal open={importOpen} onOpenChange={setImportOpen} />
    </div>
  )
}
